import { DateTime } from "luxon";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import { SxProps } from "@mui/system";
import { Theme } from "@mui/material/styles";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { FormControl, FormHelperText } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { autovioColors } from "../misc/backofficeTheme";

interface DateTimeInputProps<T extends FieldValues> {
  label?: string;
  source: Path<T>;
  allowFutureDates?: boolean;
  allowPastDates?: boolean;
  sx?: SxProps<Theme>;
  size?: "small";
  warning?: string | null;
}

export function DateTimeInput<T extends FieldValues>(props: DateTimeInputProps<T>) {
  const {
    source,
    allowFutureDates = true,
    allowPastDates = false,
    sx,
    label = "Datum & Uhrzeit",
    size,
    warning,
  } = props;
  const { control } = useFormContext<T>();

  return (
    <Controller
      name={source}
      control={control}
      render={({ field, formState }) => {
        const error = formState.errors[source]?.message;
        const errorMessage = typeof error === "string" ? error : undefined;
        return (
          <FormControl>
            <DateTimePicker
              sx={sx}
              label={label}
              inputRef={field.ref}
              ampm={false}
              value={field.value ?? null}
              disabled={formState.isSubmitting}
              minutesStep={5}
              slotProps={{
                textField: {
                  required: true,
                  size: size,
                  onBlur: field.onBlur,
                  ...(errorMessage ? { error: true, helperText: errorMessage } : { error: false }),
                },
              }}
              onChange={field.onChange}
            />
            {warning && (
              <FormHelperText sx={{ display: "flex", color: autovioColors.orange }}>
                <WarningAmberRoundedIcon sx={{ width: "16px", height: "16px", pr: "5px" }} />
                {warning}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
      rules={{
        validate: (value) => {
          if (!value) {
            return "Bitte wähle das Datum und die Uhrzeit aus.";
          }
          const dateTimeValue = value as DateTime;
          if (!DateTime.isDateTime(dateTimeValue) || !dateTimeValue.isValid) {
            return "Bitte wähle das Datum und die Uhrzeit aus.";
          }
          if (dateTimeValue.hour === 0 && dateTimeValue.minute === 0) {
            return "Bitte wähle noch die Uhrzeit aus.";
          } else if (dateTimeValue < DateTime.now() && !allowPastDates) {
            return "Bitte wähle einen Zeitpunkt in der Zukunft aus.";
          } else if (dateTimeValue > DateTime.now() && !allowFutureDates) {
            return "Bitte wähle einen Zeitpunkt in der Vergangenheit aus.";
          } else {
            return true;
          }
        },
      }}
    />
  );
}
